import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useFetcher } from '@remix-run/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FormBody from '~/pages/forms/FormBody';
import FormLoader from '~/pages/forms/FormLoader';
import FormSubmissionMessage from '~/pages/forms/FormSubmissionMessage';

import FullPageModal from '~/components/Modal/FullPageModal';
import Text from '~/components/Text';

import type { PublicationForm } from '~/interfaces/form';
import type { Publication } from '~/interfaces/publication';

import usePostSubscribeForm from '~/hooks/useActions/usePostSubscribeForm';
import useToast from '~/hooks/useToast';

interface ActionProps {
  formId: string;
  email: string;
  publication: Publication;
  onClose: () => void;
}

interface Props {
  publication: Publication;
  form: PublicationForm;
  email: string;
  onClose: () => void;
}

const PostSubscribeForm = ({ publication, form, email, onClose }: Props) => {
  const [submissionFinished, setSubmissionFinished] = useState(false);
  const fetcher = useFetcher();
  const fetcherData = fetcher.data;
  const isSubmitting = fetcher.state === 'submitting';
  const { t } = useTranslation();

  useToast(fetcherData?.toast);

  useEffect(() => {
    if (fetcherData?.redirect) {
      onClose();
    }

    if (fetcherData?.toast?.status === 'success') {
      setSubmissionFinished(true);
    }
  }, [fetcherData?.toast, submissionFinished, fetcherData?.redirect]);

  return (
    <div className="pt-8">
      {!submissionFinished ? (
        <div className="relative">
          <div className="absolute right-0 top-0">
            <button type="button" onClick={onClose}>
              <div className="flex items-center justify-center">
                <XMarkIcon className="h-6 w-6 text-gray-400 transition-all hover:text-gray-700" />
              </div>
            </button>
          </div>
          <fetcher.Form method="post" action="/post_subscribe_form">
            <FormBody email={email} form={form} publication={publication} isSubmitting={isSubmitting} />
          </fetcher.Form>
        </div>
      ) : (
        <FormSubmissionMessage
          message={form.thank_you_message || 'Thank you for your submission.'}
          logo={publication.logo.thumb.url}
          logoAlt={publication.name}
        >
          <button type="button" onClick={onClose}>
            <div className="flex items-center justify-center space-x-1">
              <Text
                as="span"
                theme="default"
                className="font-regular flex w-full cursor-pointer items-center text-sm text-wt-primary opacity-75 hover:opacity-100"
              >
                {t('shared.cta.continue')} <ChevronRightIcon className="ml-2 h-4 w-4" />
              </Text>
            </div>
          </button>
        </FormSubmissionMessage>
      )}
    </div>
  );
};

const PostSubscribeFormAction = ({ publication, formId, email, onClose }: ActionProps) => {
  const { isLoading, data } = usePostSubscribeForm({
    formId,
    email,
  });

  return (
    <FullPageModal isOpen onClose={() => {}}>
      {isLoading && !data ? (
        <FormLoader name={publication.name} />
      ) : (
        <div>
          {data && (
            <PostSubscribeForm onClose={onClose} email={email} publication={data.publication} form={data.form} />
          )}
        </div>
      )}
    </FullPageModal>
  );
};

export default PostSubscribeFormAction;
